import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Inbox, Mail, Send, FolderKanban, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_STYLES = {
  new: { bg: "#B5793F", label: "New" },
  contacted: { bg: "#19B5DE", label: "Contacted" },
  closed: { bg: "#8A7E6B", label: "Closed" },
};

function Thread({ messages }) {
  return (
    <div className="mt-4" data-testid="service-requests-list">
      {messages.map((m, i) => (
        <div key={i} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"}`}>
          <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
               style={m.from === "admin"
                 ? { background: "#262016", color: "#F6F1E5" }
                 : { background: "rgba(255,255,255,0.9)", color: "#262016", border: "1px solid rgba(38,32,22,0.1)" }}>
            {m.text}
            <span className="block mt-1.5 text-[10px] opacity-60">
              {m.from === "admin" ? "You" : "Client"} · {new Date(m.ts).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function RequestCard({ it, onChanged }) {
  const navigate = useNavigate();
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const st = STATUS_STYLES[it.status || "new"];

  const convert = async () => {
    const r = await fetch(`${API}/api/admin/projects/convert/${it.id}`, { method: "POST", credentials: "include" });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success("Project created — opening Projects"); navigate("/projects"); }
    else toast.error(d.detail || "Could not convert");
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    const r = await fetch(`${API}/api/admin/services/inquiries/${it.id}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ text: reply.trim() }),
    });
    setSending(false);
    if (r.ok) { setReply(""); toast.success("Reply sent — client emailed too"); onChanged(); }
    else toast.error("Could not send reply");
  };

  const setStatus = async (status) => {
    const r = await fetch(`${API}/api/admin/services/inquiries/${it.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success(`Marked as ${status}`); onChanged(); }
    else toast.error("Could not update");
  };

  const remove = async () => {
    if (!window.confirm("Delete this request and its conversation?")) return;
    const r = await fetch(`${API}/api/admin/services/inquiries/${it.id}`, { method: "DELETE", credentials: "include" });
    if (r.ok) { toast.success("Deleted"); onChanged(); } else toast.error("Could not delete");
  };

  return (
    <article data-testid={`inquiry-${it.id}`} className="bg-white rounded-2xl p-5 border border-[#d4cbb8] shadow-sm" style={{ marginBottom: "20px" }}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 text-[#F6F1E5]" style={{ background: st.bg }}>
          {st.label}
        </span>
        <span className="text-sm font-semibold text-[#262016]">{it.name}</span>
        <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 text-xs text-[#B5793F] hover:underline"
           data-testid={`inquiry-email-${it.id}`}>
          <Mail size={12} /> {it.email}
        </a>
        <span className="text-[11px] text-[#8A7E6B] ml-auto">
          {new Date(it.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>

      <Thread messages={it.messages || []} />

      <div className="mt-4 flex gap-2">
        <textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to the client — they get it by email and in their Tribe dashboard…"
                  data-testid={`inquiry-reply-input-${it.id}`}
                  className="flex-1 rounded-xl border border-[#26201620] bg-white px-4 py-2.5 text-sm text-[#262016] outline-none focus:border-[#B5793F]" />
        <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid={`inquiry-reply-send-${it.id}`}
                className="self-end inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm text-[#F6F1E5] disabled:opacity-40"
                style={{ background: "#262016" }}>
          <Send size={13} /> {sending ? "Sending…" : "Reply"}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <select value={it.status || "new"} onChange={(e) => setStatus(e.target.value)}
                data-testid={`inquiry-status-${it.id}`}
                className="text-xs rounded-full border border-[#26201625] bg-white px-3 py-1.5 text-[#262016] outline-none">
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="closed">Closed</option>
        </select>
        {(it.status === "closed" || it.status === "contacted") && (
          <button onClick={convert} data-testid={`inquiry-convert-${it.id}`}
                  className="inline-flex items-center gap-1.5 text-xs rounded-full px-4 py-1.5 text-[#F6F1E5]"
                  style={{ background: "#B5793F" }}>
            <FolderKanban size={13} /> Convert to project
          </button>
        )}
        <button onClick={remove} data-testid={`inquiry-delete-${it.id}`}
                className="ml-auto inline-flex items-center gap-1 text-xs text-[#8A7E6B] hover:text-red-700 transition-colors">
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </article>
  );
}

export default function AdminServices() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = () =>
    fetch(`${API}/api/admin/services/inquiries`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setItems)
      .catch(() => setItems(false));

  useEffect(() => { load(); }, []);

  if (items === null) return <p className="rp-display rp-italic text-[#5C5042]">One moment…</p>;
  if (items === false) return <p className="text-sm text-[#8A7E6B]">Could not load service requests.</p>;

  return (
    <div data-testid="admin-services">
      <div className="flex items-center gap-2 mb-5">
        <Inbox size={16} className="text-[#B5793F]" />
        <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7E6B] font-medium">
          Service requests — {items.length} total · {items.filter((x) => (x.status || "new") === "new").length} new
        </p>
        <button onClick={() => navigate("/projects")} data-testid="open-projects-btn"
                className="ml-auto inline-flex items-center gap-1.5 text-xs rounded-full px-4 py-2 text-[#F6F1E5]"
                style={{ background: "#262016" }}>
          <FolderKanban size={13} /> Projects <ArrowUpRight size={12} />
        </button>
      </div>

      {items.length === 0 && (
        <p className="text-sm text-[#8A7E6B]">No requests yet. Share the /services link to start receiving briefs.</p>
      )}

      <div className="space-y-6">
        {items.map((it) => <RequestCard key={it.id} it={it} onChanged={load} />)}
      </div>
    </div>
  );
}
